import { useState, useEffect, ChangeEvent } from "react";
import {
  Avatar,
  Button,
  Card,
  CardBody,
  Center,
  ChakraProvider,
  CloseButton,
  Flex,
  InputRightElement,
  Link,
  SimpleGrid,
  TableProps,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  StackDivider,
} from "@chakra-ui/react";
import "./App.css";
import { theme } from "@chakra-ui/pro-theme";
import { extendTheme } from "@chakra-ui/react";
import {
  Box,
  Container,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
interface ReturnResultFAQ {
  link: string;
  title: string;
}
interface ReturnResult {
  internalID: string;
  bankName: string;
  bankID?: string;
  logo: string;
  integration: string;
  country?: string;
  initials: string;
  faqs?: ReturnResultFAQ[];
}

interface BankTableProps extends TableProps {
  bankResults: ReturnResult[];
  showOutBankModal: (result: ReturnResult) => void;
  showFAQModal: (result: ReturnResult) => void;
}

interface BankCardProps {
  bankResult: ReturnResult | null;
  showButtons?: boolean;
  showOutBankModal?: (result: ReturnResult) => void;
  showFAQModal?: (result: ReturnResult) => void;
  variant?: string;
  children?: React.ReactNode;
}

interface ReturnResultProps {
  bankResult: ReturnResult | null;
}

const FAQContent = (props: ReturnResultProps) => {
  const { bankResult } = props;
  if (!bankResult || !bankResult.faqs || bankResult.faqs.length === 0) {
    return null;
  }
  return (
    <Box mb={5}>
      {bankResult.faqs.map((faq, i) => (
        <Link key={`faq${i}`} href={faq.link} isExternal>
          <Text size="xs">
            {"\u2022"}&nbsp;
            {faq.title}
          </Text>
        </Link>
      ))}
    </Box>
  );
};

const BankCard = (props: BankCardProps) => {
  const {
    bankResult,
    showOutBankModal,
    showFAQModal,
    showButtons,
    variant,
    children,
  } = props;
  if (!bankResult) {
    return null;
  }
  return (
    <Card bgColor="white" variant={variant ?? "elevated"} key={bankResult.internalID} borderRadius={10}>
      <CardBody px={3}>
        <Stack divider={<StackDivider />} spacing="4">
          <Flex>
            <Flex flex="1" gap="4" alignItems="center" flexWrap="nowrap">
              <Avatar
                size="lg"
                name={bankResult.initials}
                src={bankResult.logo}
                color={"white"}
              />
              <Box>
                <Text as="b" noOfLines={1} color="#333333">
                  {bankResult.bankName}
                </Text>
                <Text color="#333333">
                  {bankResult.country
                    ? `${getUnicodeFlagIcon(bankResult.country)} `
                    : ""}
                  {bankResult.bankID}
                </Text>

                {showButtons === true ? (
                  <Button
                    size="xs"
                    variant="solid"
                    color="white"
                    bgColor="#005aa0"
                    onClick={() => {
                      if (showOutBankModal) {
                        showOutBankModal(bankResult);
                      }
                    }}
                  >
                    In Outbank einbinden
                  </Button>
                ) : null}
                {showButtons === true &&
                bankResult.faqs?.length &&
                bankResult.faqs.length > 0 ? (
                  <Button
                    ml="1"
                    size="xs"
                    variant="solid"
                    color="white"
                    bgColor="#005aa0"
                    onClick={() => {
                      if (showFAQModal) {
                        showFAQModal(bankResult);
                      }
                    }}
                  >
                    FAQ
                  </Button>
                ) : null}
              </Box>
            </Flex>
          </Flex>
          {children ? children : null}
        </Stack>
      </CardBody>
    </Card>
  );
};

const BankTable = (props: BankTableProps) => {
  const { showOutBankModal, showFAQModal, bankResults } = props;
  return (
    <SimpleGrid minChildWidth={300} spacing={3}>
      {bankResults.map((bankResult) => (
        <BankCard
          key={bankResult.internalID}
          bankResult={bankResult}
          showOutBankModal={showOutBankModal}
          showFAQModal={showFAQModal}
          showButtons={true}
        />
      ))}
    </SimpleGrid>
  );
};

function App() {
  const [searchResult, setSearchResult] = useState<ReturnResult[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [searchTopic, setSearchTopic] = useState<string>("");
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [openInOutBankModalVisible, setOpenInOutBankModalVisible] =
    useState<boolean>(false);
  const [openBankFAQModalVisible, setOpenBankFAQModalVisible] =
    useState<boolean>(false);
  const [selectedBank, setSelectedBank] = useState<ReturnResult | null>(null);

  const showOutBankModal = (result: ReturnResult) => {
    setOpenInOutBankModalVisible(true);
    setSelectedBank(result);
  };

  const showFAQModal = (result: ReturnResult) => {
    setOpenBankFAQModalVisible(true);
    setSelectedBank(result);
  };

  const closeOutBankModal = () => {
    setOpenInOutBankModalVisible(false);
    setSelectedBank(null);
  };

  const closeFAQModal = () => {
    setOpenBankFAQModalVisible(false);
    setSelectedBank(null);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    if (timerId) {
      clearTimeout(timerId);
    }

    setTimerId(
      setTimeout(() => {
        setSearchTopic(value);
      }, 500)
    );
  };

  const resetInput = () => {
    setInputValue(""); // reset input value
    setSearchTopic(""); // reset debounced value
  };

  useEffect(() => {
    if (searchTopic === "") {
      setSearchResult([]);
      return;
    }
    // fetch(`http://localhost:4000/search?term=${searchTopic}`, {
    fetch(`https://banksearch.outbankapp.com/search?term=${searchTopic}`, {
      method: "GET",
      headers: {
        "X-Key": "93sjadf98320hg02asadfh083414",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setSearchResult(data);
      })
      .catch((error) => console.log(error));
  }, [searchTopic]);

  const proTheme = extendTheme(theme);
  const extendedConfig = {
    colors: { ...proTheme.colors, brand: proTheme.colors.black },
    fonts: {
      heading: "Poppins, Arial, Helvetica, sans-serif",
      body: "Poppins, Arial, Helvetica, sans-serif",
    },
  };
  const myTheme = extendTheme(extendedConfig, proTheme);
  return (
    <ChakraProvider theme={myTheme}>
      <Box as="section" margin={0} padding={0}>
        <Card bgColor="f4f7fb" variant="filled" margin={0} pb={5} borderRadius={0}>
          <CardBody margin={0} padding={0}>
            <Box margin={0} padding={0}>
              <Container>
                <HStack justify="space-between" mb={5}>
                  <InputGroup
                  // maxW={{ md: "sm", lg: "md" }}
                  //display={{ base: "none", md: "inline-flex" }}
                  >
                    <InputLeftElement>
                      <Icon
                        as={FiSearch}
                        color="#333333"
                        fontSize="lg"
                      />
                    </InputLeftElement>

                    <Input
                      placeholder="Bankname, BLZ oder Stadt eingeben"
                      variant="filled.accent"
                      color="#333333"
                      bgColor="#ffffff"
                      value={inputValue}
                      borderRadius={10}
                      borderColor="#e0dede"
                      _placeholder={{ color: "#676772" }}
                      _hover={{ borderColor: "#005aa0" }} 
                      onChange={(e) => handleInputChange(e)}
                    />
                    <InputRightElement>
                      <CloseButton
                        size="xs"
                        onClick={() => resetInput()}
                        color="#333333"
                      />
                    </InputRightElement>
                  </InputGroup>
                </HStack>
                {searchResult.length > 0 && searchTopic !== "" ? (
                  <BankTable
                    bankResults={searchResult}
                    showFAQModal={showFAQModal}
                    showOutBankModal={showOutBankModal}
                    variant="filled.accent"
                  />
                ) : null}
                {searchResult.length === 0 && searchTopic !== "" ? (
                  <Center>
                    <Text>
                      Du findest deinen Anbieter nicht? Dann lass uns das gerne
                      via{" "}
                      <Link
                        href={`mailto:help@outbankapp.com?subject=${
                          'Bank "' + searchTopic + '" wird nicht unterstützt'
                        }`}
                        isExternal
                      >
                        E-Mail{" "}
                      </Link>
                      wissen.
                    </Text>
                  </Center>
                ) : null}
              </Container>
            </Box>
          </CardBody>
        </Card>
      </Box>
      <Modal
        isOpen={openInOutBankModalVisible}
        onClose={closeOutBankModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent zIndex={9999}>
          <ModalHeader>In Outbank öffnen</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <BankCard
              variant="unstyled"
              bankResult={selectedBank}
              showButtons={false}
            >
              <Text>
                Hast du Outbank schon installiert? Dann kannst du einfach den
                folgenden Button nutzen um den Zugang für{" "}
                {selectedBank?.bankName} automatisch einzurichten.
                <br />
                <Center my={5}>
                  <Link href={selectedBank?.integration ?? "#"} isExternal>
                    <Button
                      size="xs"
                      variant="solid"
                      color="white"
                      bgColor="#005aa0"
                    >
                      In Outbank einbinden
                    </Button>
                  </Link>
                </Center>
              </Text>
              <Text>
                Wenn du Outbank noch nicht installiert hast, nutze folgenden
                Button um Outbank zu installieren.
                <br />
                <Center my={5}>
                  <Link href="https://outbankapp.com/herunterladen/" isExternal>
                    <Button
                      size="xs"
                      variant="solid"
                      color="white"
                      bgColor="#005aa0"
                    >
                      Outbank installieren
                    </Button>
                  </Link>
                </Center>
              </Text>
            </BankCard>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        size={"xl"}
        isOpen={openBankFAQModalVisible}
        onClose={closeFAQModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent zIndex={9999}>
          <ModalHeader>Häufig gestellte Fragen</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <BankCard
              variant="unstyled"
              bankResult={selectedBank}
              showButtons={false}
            >
              <FAQContent bankResult={selectedBank} />
            </BankCard>
          </ModalBody>
        </ModalContent>
      </Modal>
    </ChakraProvider>
  );
}

export default App;
